import { BirthSex, birthDateError, readBirthSex, withBirthSex } from '@bariendo/lib/birth-details';
import { NORMAL_CONFIDENTIALITY_LEVEL_CODING, PATIENT_PROFILE_UPDATED_REASON } from '@bariendo/lib/codings';
import { createReference, getReferenceString, MedplumClient } from '@medplum/core';
import { Patient, Provenance } from '@medplum/fhirtypes';
import { PatientEdit } from '@/pages/PatientTimelinePage/context-hooks/usePatient';
import { reportError } from '@/services/error-reporting';
import {
  HomeAddress,
  homeAddressError,
  homeAddressIsFirst,
  readHomeAddress,
  sameHomeAddress,
  withHomeAddress,
} from '@/services/patient-address';
import { readLegalName, readPreferredName, withPatientName } from '@/services/patient-name';

/**
 * One Patient field confirmed or corrected on the PN consultation card (BAR-2927, BAR-2928,
 * BAR-3001) or the header's Edit name (BAR-2993). An empty `preferredName` is no edit; removing
 * the preferred name is its own edit, so clearing a box never deletes one by accident.
 */
export type PatientProfileEdit =
  | { field: 'legalName'; value: { given: string; family: string } }
  | { field: 'preferredName'; value: string }
  | { field: 'removePreferredName' }
  | { field: 'birthDate'; value: string }
  | { field: 'birthSex'; value: BirthSex }
  | { field: 'homeAddress'; value: HomeAddress };

const FIELD_NAMES: Record<PatientProfileEdit['field'], string> = {
  legalName: 'Legal name',
  preferredName: 'Preferred name',
  removePreferredName: 'Preferred name',
  birthDate: 'Date of birth',
  birthSex: 'Sex assigned at birth',
  homeAddress: 'Home address',
};

/**
 * Saves one profile edit through the patient context's `applyPatientEdit`
 * (Orbit's Patient write path, version-checked), then records who made it as a
 * Provenance.
 *
 * Writes nothing when the chart already holds the value. Throws when the value
 * is invalid or the Patient write fails, so the caller can say it did not save.
 * The Provenance fails soft: the Patient is already saved by then, and telling
 * the navigator otherwise would only get the same edit made twice.
 */
export async function savePatientProfileEdit(
  medplum: MedplumClient,
  args: {
    patient: Patient;
    applyPatientEdit: (edit: PatientEdit) => Promise<void>;
    edit: PatientProfileEdit;
  }
): Promise<void> {
  const { patient, applyPatientEdit, edit } = args;

  let change: PatientEdit;
  if (edit.field === 'legalName') {
    const given = edit.value.given.trim();
    const family = edit.value.family.trim();
    if (!given && !family) throw new Error('Legal name cannot be empty.');
    const current = readLegalName(patient);
    if (current.given === given && current.family === family) return;
    // The whole name list, rebuilt from whichever copy the change is applied to,
    // so a retry keeps a name entry written since this Patient was read.
    change = (fresh) => ({ name: withPatientName(fresh.name, { kind: 'legal', given, family }) });
  } else if (edit.field === 'preferredName') {
    const name = edit.value.trim();
    if (!name || readPreferredName(patient) === name) return;
    // Alone, it would become name[0], which every reader takes as the patient's name.
    const legal = readLegalName(patient);
    if (!legal.given && !legal.family) throw new Error('Add the legal name first.');
    change = (fresh) => ({ name: withPatientName(fresh.name, { kind: 'preferred', name }) });
  } else if (edit.field === 'removePreferredName') {
    if (!patient.name?.some((name) => name.use === 'usual')) return;
    change = (fresh) => ({ name: withPatientName(fresh.name, { kind: 'removePreferred' }) });
  } else if (edit.field === 'birthDate') {
    const error = birthDateError(edit.value);
    if (error) throw new Error(error);
    if (patient.birthDate === edit.value) return;
    const birthDate = edit.value;
    change = () => ({ birthDate });
  } else if (edit.field === 'homeAddress') {
    const error = homeAddressError(edit.value);
    if (error) throw new Error(error);
    // A confirmed address still moves to the front, where readers of address[0] find it.
    if (sameHomeAddress(readHomeAddress(patient), edit.value) && homeAddressIsFirst(patient)) return;
    const value = edit.value;
    change = (current) => ({ address: withHomeAddress(current.address, value) });
  } else {
    if (readBirthSex(patient) === edit.value) return;
    // The whole list, rebuilt from whichever copy the change is applied to (a
    // retry applies it to a fresh one), so no other extension is dropped.
    const code = edit.value;
    change = (current) => ({ extension: withBirthSex(current.extension, code) });
  }

  await applyPatientEdit(change);

  const profile = medplum.getProfile();
  if (!profile) return;

  try {
    await medplum.createResource<Provenance>({
      resourceType: 'Provenance',
      // PN may write Provenance only under its confidentiality clearance;
      // unlabelled, the create is refused.
      meta: { security: [NORMAL_CONFIDENTIALITY_LEVEL_CODING] },
      // No `display`: createReference would copy the patient's name into the Provenance.
      target: [{ reference: getReferenceString(patient) }],
      recorded: new Date().toISOString(),
      // Names the field, never its value: Provenance is readable more widely than the chart.
      reason: [{ coding: [PATIENT_PROFILE_UPDATED_REASON], text: `${FIELD_NAMES[edit.field]} updated` }],
      agent: [{ who: createReference(profile) }],
    });
  } catch (e) {
    reportError(e, {
      component: 'savePatientProfileEdit',
      extra: { patientId: patient.id, field: edit.field, reason: 'provenance-write-failed' },
    });
  }
}
